import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Tab,
  Tabs,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  FormControlLabel,
  Switch,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../store';
import DateRangeFilter from './DateRangeFilter';

import { can } from '../../utils/access';
import { formatWorkHoursSummary, workHoursError } from '../../utils/weekdayHours';
import WeekdayHoursEditor from '../shared/WeekdayHoursEditor';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Info Tab Component
function InfoTab({ employee, onEdit, onLinkUser, onUnlinkUser, linkableUsers, loadingLinkableUsers, canManageLinks, linking, t }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>{t('Basic Information')}</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={onEdit}
        >
          {t('Edit')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('Name')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {employee.name}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('Status')}
            </Typography>
            <Chip
              label={employee.state === 'active' ? t('Active') : t('Inactive')}
              color={employee.state === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Paper>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('Show in calendar')}
            </Typography>
            <Chip
              label={employee.show_in_calendar === false ? t('No') : t('Yes')}
              color={employee.show_in_calendar === false ? 'default' : 'success'}
              size="small"
            />
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('employee_title')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {employee.title || t(`role_${employee.role || 'employee'}`)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('Phone')}
                </Typography>
                <Typography variant="body1">
                  {employee.phone_number || '-'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('Linked user')}
                </Typography>
                {employee.user_email ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ flex: 1 }}>
                      {employee.user_email}
                    </Typography>
                    {canManageLinks && (
                      <IconButton
                        size="small"
                        onClick={onUnlinkUser}
                        disabled={linking}
                        title={t('Unlink user')}
                      >
                        {linking ? <CircularProgress size={18} /> : <LinkOffIcon fontSize="small" />}
                      </IconButton>
                    )}
                  </Box>
                ) : canManageLinks ? (
                  <Autocomplete
                    size="small"
                    options={linkableUsers || []}
                    getOptionLabel={(option) => option.email || ''}
                    loading={loadingLinkableUsers}
                    onChange={(e, value) => { if (value) onLinkUser(value); }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoComplete="off"
                        placeholder={t('Select user to link...')}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingLinkableUsers && <CircularProgress color="inherit" size={18} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {option.email}
                          </Typography>
                          {option.name && (
                            <Typography variant="caption" color="text.secondary">
                              {option.name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    disabled={linking}
                  />
                ) : (
                  <Typography variant="body1">
                    {t('No linked user')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// Performance Tab Component
function PerformanceTab({ employeeId, t, onMaintenanceClick }) {
  const [stats, setStats] = useState(null);
  // The API leaves the money out without reports.view (Raporttien katselu); follow the payload.
  const showRevenue = stats?.revenue?.total_revenue !== undefined;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default to current month
  const getDefaultDateRange = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      preset: 'thisMonth',
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  const fetchStats = useCallback(async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/scheduling/employee-stats/${employeeId}/`, {
        params: {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
        },
      });
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [employeeId, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(num);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Date Range Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {t('Time period')}
        </Typography>
        <DateRangeFilter
          value={dateRange}
          onChange={setDateRange}
          selectedPreset={dateRange.preset}
        />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: 90,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 700,
                color: 'primary.main',
                lineHeight: 1.2,
              }}
            >
              {stats?.work_hours?.total || 0}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mt: 0.5 }}
            >
              {t('Work hours')}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: 90,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 700,
                color: 'success.main',
                lineHeight: 1.2,
              }}
            >
              {stats?.revenue?.maintenance_count || 0}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mt: 0.5 }}
            >
              {t('Maintenances')}
            </Typography>
          </Paper>
        </Grid>

        {showRevenue && (
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 90,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {formatCurrency(stats?.revenue?.total_revenue)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mt: 0.5 }}
              >
                {t('Total revenue')}
              </Typography>
            </Paper>
          </Grid>
        )}

        {showRevenue && (
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 90,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {formatCurrency(stats?.revenue?.labor_revenue)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mt: 0.5 }}
              >
                {t('Labor revenue')}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Revenue breakdown */}
      {showRevenue && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t('Revenue breakdown')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">{t('Parts')}</Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatCurrency(stats?.revenue?.parts_revenue)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">{t('Labor')}</Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatCurrency(stats?.revenue?.labor_revenue)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">{t('Avg per job')}</Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatCurrency(stats?.revenue?.average_per_maintenance)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Recent Maintenances */}
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {t('Recent maintenances')}
      </Typography>
      {stats?.maintenances?.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('Work #')}</TableCell>
                <TableCell>{t('Vehicle')}</TableCell>
                <TableCell>{t('Customer')}</TableCell>
                <TableCell>{t('Status')}</TableCell>
                {showRevenue && <TableCell align="right">{t('Revenue')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.maintenances.map((m) => (
                <TableRow
                  key={m.id}
                  hover
                  onClick={() => onMaintenanceClick?.(m.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{m.work_number || m.id}</TableCell>
                  <TableCell>
                    {m.vehicle?.registration_number || '-'}
                    {m.vehicle?.manufacturer && ` (${m.vehicle.manufacturer})`}
                  </TableCell>
                  <TableCell>{m.customer?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(m.state)}
                      size="small"
                      color={m.state === 'picked_up' ? 'success' : 'primary'}
                      variant="outlined"
                    />
                  </TableCell>
                  {showRevenue && <TableCell align="right">{formatCurrency(m.total_revenue)}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {t('No maintenances in this period')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

// Settings Tab Component
function SettingsTab({ employee, onUpdate, t }) {
  // Per-weekday hours in the editor: the employee's own, or the effective ones (the workshop's)
  // to start from. Saving makes them personal, so Save is enabled only after an edit.
  const [workHours, setWorkHours] = useState(employee.work_hours || employee.effective_work_hours);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // The dialog refetches the employee after a save: show what the server now has
  useEffect(() => {
    setWorkHours(employee.work_hours || employee.effective_work_hours);
    setDirty(false);
  }, [employee]);

  // Own hours in either form: per weekday, or the older single period / weekday list
  const hasPersonalShift = !!(
    employee.work_hours || employee.work_day_start || employee.work_day_end || employee.working_weekdays
  );

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    const workHoursProblem = workHoursError(workHours);
    if (workHoursProblem) {
      setError(t(workHoursProblem));
      return;
    }
    setSaving(true);

    try {
      await axios.patch(`/api/employees/${employee.id}/`, { work_hours: workHours });
      setDirty(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.work_hours?.[0] || t('Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    setError(null);

    try {
      await axios.patch(`/api/employees/${employee.id}/`, {
        work_hours: null,
        work_day_start: null,
        work_day_end: null,
        working_weekdays: null,
      });
      setDirty(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.detail || t('Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('Shift Settings')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('Set personal work hours for this employee. These override the organization defaults.')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{t('Settings saved successfully')}</Alert>}

      {/* Organization defaults display */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          {t('Organization default')}: {employee.organization_work_hours
            ? formatWorkHoursSummary(employee.organization_work_hours, t)
            : `${employee.effective_work_day_start || '08:00'} - ${employee.effective_work_day_end || '16:00'}`}
        </Typography>
        {hasPersonalShift && employee.effective_work_hours && (
          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500, mt: 0.5 }}>
            {t('Your current setting')}: {formatWorkHoursSummary(employee.effective_work_hours, t)}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('workHours.personalHelp')}
        </Typography>
      </Paper>

      {/* Work hours per weekday */}
      <Box sx={{ mb: 3 }}>
        <WeekdayHoursEditor
          value={workHours}
          onChange={(value) => { setWorkHours(value); setDirty(true); }}
          closedLabel={t('workHours.dayOff')}
          testIdPrefix="employee-work-hours"
        />
      </Box>

      {/* Save buttons */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? <CircularProgress size={20} /> : <span>{t('Save')}</span>}
        </Button>
        {hasPersonalShift && (
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={saving}
          >
            {t('Use organization default')}
          </Button>
        )}
      </Box>
    </Box>
  );
}

// Main Dialog Component
function EmployeeDetailsDialog({ open, onClose, employee, onEmployeeUpdated }) {
  const { t } = useTranslation();
  const theme = useTheme();
  // Full screen on a phone: the settings tab's weekday rows need the whole width
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const [tab, setTab] = useState(0);
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone_number: '', title: '', show_in_calendar: true });
  const [editError, setEditError] = useState(null);
  const [editNameError, setEditNameError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(false);
  const [linkableUsers, setLinkableUsers] = useState([]);
  const [loadingLinkableUsers, setLoadingLinkableUsers] = useState(false);
  const [linking, setLinking] = useState(false);

  const canManageLinks = can(currentOrganization, 'team.manage');

  // Fetch fresh employee data including shift settings
  // silent: refresh in place without the loading spinner, which would unmount the open tab (and
  // with it the settings tab's "saved" message)
  const fetchEmployeeData = useCallback(async ({ silent = false } = {}) => {
    if (!employee?.id) return;

    try {
      if (!silent) setLoading(true);
      // Get employee stats which includes full employee data
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const response = await axios.get(`/api/scheduling/employee-stats/${employee.id}/`, {
        params: {
          start_date: startOfMonth.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
        },
      });
      setEmployeeData(response.data.employee);
    } catch (err) {
      // Fallback to basic employee data
      setEmployeeData(employee);
    } finally {
      setLoading(false);
    }
  }, [employee]);

  const fetchLinkableUsers = useCallback(async () => {
    try {
      setLoadingLinkableUsers(true);
      const response = await axios.get('/api/employees/linkable-users/');
      setLinkableUsers(response.data);
    } catch (err) {
      setLinkableUsers([]);
    } finally {
      setLoadingLinkableUsers(false);
    }
  }, []);

  useEffect(() => {
    if (open && employee) {
      fetchEmployeeData();
      setTab(0);
      setConfirmDisable(false);
      setEditMode(false);
      if (canManageLinks) {
        fetchLinkableUsers();
      }
    }
  }, [open, employee, fetchEmployeeData, canManageLinks, fetchLinkableUsers]);

  const handleLinkUser = async (user) => {
    try {
      setLinking(true);
      await axios.post(`/api/employees/${employee.id}/link-user/`, { user_id: user.id });
      fetchEmployeeData();
      fetchLinkableUsers();
      if (onEmployeeUpdated) onEmployeeUpdated();
    } catch (err) {
      console.error('Error linking user:', err);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkUser = async () => {
    try {
      setLinking(true);
      await axios.post(`/api/employees/${employee.id}/unlink-user/`);
      fetchEmployeeData();
      fetchLinkableUsers();
      if (onEmployeeUpdated) onEmployeeUpdated();
    } catch (err) {
      console.error('Error unlinking user:', err);
    } finally {
      setLinking(false);
    }
  };

  const handleDisable = async () => {
    if (!confirmDisable) {
      setConfirmDisable(true);
      return;
    }

    try {
      setDisabling(true);
      await axios.post(`/api/employees/${employee.id}/disable/`);
      if (onEmployeeUpdated) onEmployeeUpdated();
      onClose();
    } catch (err) {
      console.error('Error disabling employee:', err);
    } finally {
      setDisabling(false);
    }
  };

  const handleEnable = async () => {
    try {
      setDisabling(true);
      await axios.post(`/api/employees/${employee.id}/enable/`);
      if (onEmployeeUpdated) onEmployeeUpdated();
      onClose();
    } catch (err) {
      console.error('Error enabling employee:', err);
    } finally {
      setDisabling(false);
    }
  };

  const handleEditSave = async () => {
    try {
      setEditError(null);
      setEditNameError(null);
      await axios.patch(`/api/employees/${employee.id}/`, editForm);
      setEditMode(false);
      setEditSuccess(true);
      fetchEmployeeData();
      if (onEmployeeUpdated) onEmployeeUpdated();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.name?.[0]) {
        // e.g. the name is already used by another (possibly deactivated) employee
        setEditNameError(errorData.name[0]);
      } else {
        setEditError(errorData?.detail || errorData?.non_field_errors?.[0] || t('Failed to save employee'));
      }
    }
  };

  const startEdit = () => {
    setEditError(null);
    setEditNameError(null);
    setEditForm({
      name: employeeData?.name || employee?.name || '',
      phone_number: employeeData?.phone_number || employee?.phone_number || '',
      title: employeeData?.title || employee?.title || '',
      show_in_calendar: (employeeData || employee)?.show_in_calendar ?? true,
    });
    setEditMode(true);
  };

  const currentEmployee = employeeData || employee;

  if (!employee) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {currentEmployee?.name}
          </Typography>
          <Chip
            label={currentEmployee?.state === 'active' ? t('Active') : t('Inactive')}
            color={currentEmployee?.state === 'active' ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label={t('Info')} />
          <Tab label={t('Performance')} />
          <Tab label={t('Settings')} />
        </Tabs>
      </Box>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : editMode ? (
          // Edit Mode
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('Edit Employee')}
            </Typography>

            {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}

            <TextField
              fullWidth
              label={t('Name')}
              value={editForm.name}
              onChange={(e) => {
                setEditForm({ ...editForm, name: e.target.value });
                setEditNameError(null);
              }}
              error={Boolean(editNameError)}
              helperText={editNameError}
              inputProps={{ 'data-testid': 'employee-edit-name' }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('employee_title')}
              placeholder={t('employee_title_placeholder')}
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('Phone')}
              value={editForm.phone_number}
              onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.show_in_calendar}
                  onChange={(e) =>
                    setEditForm({ ...editForm, show_in_calendar: e.target.checked })
                  }
                />
              }
              label={t('Show in calendar')}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={handleEditSave}>
                {t('Save')}
              </Button>
              <Button variant="outlined" onClick={() => setEditMode(false)}>
                {t('Cancel')}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <TabPanel value={tab} index={0}>
              <InfoTab
                employee={currentEmployee}
                onEdit={startEdit}
                onLinkUser={handleLinkUser}
                onUnlinkUser={handleUnlinkUser}
                linkableUsers={linkableUsers}
                loadingLinkableUsers={loadingLinkableUsers}
                canManageLinks={canManageLinks}
                linking={linking}
                t={t}
              />
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <PerformanceTab
                employeeId={employee.id}
                t={t}
                onMaintenanceClick={(maintenanceId) => {
                  onClose();
                  navigate(`/maintenances/${maintenanceId}`);
                }}
              />
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <SettingsTab
                employee={currentEmployee}
                onUpdate={() => fetchEmployeeData({ silent: true })}
                t={t}
              />
            </TabPanel>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Box>
          {currentEmployee?.state === 'active' ? (
            <Button
              color="error"
              variant={confirmDisable ? 'contained' : 'outlined'}
              onClick={handleDisable}
              disabled={disabling}
            >
              {disabling ? (
                <CircularProgress size={20} />
              ) : confirmDisable ? (
                t('Confirm disable')
              ) : (
                t('Disable employee')
              )}
            </Button>
          ) : (
            <Button
              color="success"
              variant="outlined"
              onClick={handleEnable}
              disabled={disabling}
            >
              {disabling ? <CircularProgress size={20} /> : <span>{t('Enable employee')}</span>}
            </Button>
          )}
          {confirmDisable && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {t('This will set the employee as inactive')}
            </Typography>
          )}
        </Box>
        <Button onClick={onClose}>{t('Close')}</Button>
      </DialogActions>

      <Snackbar
        open={editSuccess}
        autoHideDuration={3000}
        onClose={() => setEditSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setEditSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {t('Employee updated successfully')}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default EmployeeDetailsDialog;
